import {useContext, useEffect, useMemo, useRef, useState} from 'react';

import classNames from 'classnames';
import {observer} from "mobx-react";
import URI from 'urijs';

import {buildCssUrlString} from "../../../utils/StringUtilities";
import ClickableLink from "../../utilities/ClickableLink";
import {ExecutionEnvironment} from '../../../stores/InterfaceStore';
import FontAwesome from "../../utilities/FontAwesome";
import MegaNav from "../navigation/meganav/MegaNav";
import {SearchController} from "../../../backends/YiiRoutes";
import {StoreContext} from "../../../stores/StoreLoader";
import styles from "../styles/Header.module.scss"
import * as React from "react";


const SearchBox = () => {
    const {i18nStore, organizationStore} = useContext(StoreContext);
    const searchRef = useRef(null);
    const submitRef = useRef<HTMLButtonElement | null>(null);
    const [searchValue, setSearchValue] = useState<string>("");
    const [listening, setListening] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    const recognition = useMemo(() => {
        let r;
        //@ts-ignore
        if (ExecutionEnvironment.canUseDOM && (window.SpeechRecognition || window.webkitSpeechRecognition)) {
            //@ts-ignore
            const speechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            if (speechRecognition && typeof speechRecognition !== "undefined") {
                r = new speechRecognition();
                r.interimResults = true;
                r.maxAlternatives = 1;
                // r.lang = i18nStore.currentOrgSupportedLocale;
            }
        }
        return r;
    }, []);

    useEffect(() => {
        const uri = URI();
        if (uri.hasQuery("q")) {
            const parsed = URI.parseQuery(uri.toString());
            setSearchValue(parsed.q);
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            const searchInput = document.getElementById('sb-search-input-large');
            if (searchInput) searchInput.focus();
        }
    }, [isOpen])

    function handleClick(e) {
        e.preventDefault();
        if (listening && recognition) {
            recognition.abort();
            setListening(false);
        } else if (recognition) {
            recognition.start();
            setListening(true);
        }
    }

    function handleMouseDown(e) {
        // this is important to prevent focus from being shifted from the input, which then closes the search bar
        e.preventDefault();
    }

    function handleChange(e) {
        setSearchValue(e.target.value);
    }

    function handleEyeglassClick() {
        setIsOpen(true)
    }

    function handleFocus() {
        const logoCell = document.getElementById('logo-cell');
        const titleCell = document.getElementById('title-cell');
        if (logoCell && titleCell && window.innerWidth < 1216) {
            logoCell.classList.add('hidden');
            titleCell.classList.add('hidden');
        }
    }

    function handleBlur(e) {
        // if the blur was because of outside focus
        // currentTarget is the parent element, relatedTarget is the clicked element
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setIsOpen(false);
            const logoCell = document.getElementById('logo-cell');
            const titleCell = document.getElementById('title-cell');
            if (logoCell && titleCell && window.innerWidth < 1216) {
                logoCell.classList.remove('hidden');
                titleCell.classList.remove('hidden');
            }
        }
    }

    useEffect(() => {
        if (recognition) {
            recognition.onresult = function (event) {
                const text = event.results[0][0].transcript;
                setSearchValue(text);

                if (event.results[0].isFinal) {
                    setListening(false);
                    submitRef.current?.click();
                }
            };
            recognition.onerror = function(event) {
                setListening(false);
                let message: string | null = null;
                switch (event.error) {
                    case "network":
                        message = "There was a problem connecting to the network, please check your connection or browser settings to enable speech to text.";
                        break;
                    case "aborted":
                        break;
                    default:
                        message = "An error occurred! Please enable microphone access and try again!";
                        break;
                }
                if (message) {
                    alert(message);
                }
            }
        }
    }, []);

    const searchTextClassName = classNames({
        "form-control": true,
        [styles.searchText]: true,
    })
    const formGroupClassName = classNames({
        "has-feedback": true,
        [styles.formGroup]: true,
    });

    if (!isOpen) {
        return <button onClick={handleEyeglassClick} className={styles.searchIcon} aria-label={"Search this site"}>
            <FontAwesome name={'fa-search'} prefix={'fas'} size={'lg'} aria-hidden={true}/>
        </button>
    } else {
        return <form action={SearchController.search(i18nStore.currentOrgSupportedLocale)}
                     method="GET"
                     name="search"
                     acceptCharset="UTF-8"
                     className={styles.searchForm}
                     title="Website search"
                     onFocus={handleFocus}
                     onBlur={handleBlur}
        >
            <input type="hidden"
                   name="orgId"
                   value={organizationStore.organization.id}/>
            <div className={formGroupClassName}>
                <input ref={searchRef}
                       id="sb-search-input-large"
                       className={searchTextClassName}
                       name="q"
                       type="text"
                       aria-label="Search this site"
                       placeholder="Search"
                       value={searchValue}
                       onChange={handleChange}
                       autoComplete="off"
                       title="Search"
                />
                <button type="submit"
                        ref={submitRef}
                        className={styles.eyeglass}
                        title="Submit search"
                        aria-label="Submit search"
                >
                    <FontAwesome name={'fa-search'} prefix={'fas'} size={'lg'} className={styles.darkIcon} aria-role={'presentation'} aria-hidden={true}/>
                </button>
                {recognition && <button title={"Search by voice"}
                                        aria-label={"Search by voice"}
                                        onClick={handleClick}
                                        onMouseDown={handleMouseDown}
                                        className={styles.speech}
                >
                    <FontAwesome name={`${listening ? "fa-volume-down" : "fa-microphone"}`} prefix={'fas'} size={'lg'} className={styles.darkIcon} aria-role={'presentation'} aria-hidden={true}/>
                </button>}
            </div>
        </form> }
}

const SimpleAndFloating: React.FC<{
    toggleUserMenu: () => void,
    openLoginModal: () => void,
    mainUrl: string,
}> = observer(props => {
    const {organizationStore, userStore, styleStore} = useContext(StoreContext);

    const hideHeaderTitle = organizationStore.organization.json_data.settings.appearance.hideHeaderTitle;

    const megaNavContainerClassName = classNames({
        ["sb-secondary-nav sb-organization-color-secondary-nav clearfix"]: true,
        ["sb-navbar-secondary-container sb-content-width-plus-margin"]: styleStore.themeID === "2",
    })
    const logoContainerClassName = classNames({
        "sb-navbar-primary-element": true,
        "sb-navbar-primary-element-logo": true,
        "sb-navbar-logo": true,
        "notranslate": true,
    })
    const logoCellClassName = classNames({
        "sb-navbar-cell": true,
        "sb-navbar-logo-smallwidth": true,
    })
    const titleAndSocialCellClassName = classNames({
        "sb-navbar-cell": true,
        [styles.titleAndSocial]: true,
        [styles.titleAndSocialTitleInvisible]: hideHeaderTitle,
    })
    const primaryNavContainerClassName = classNames({
        "sb-navbar-primary-container": true,
        "clearfix": true,
        "sb-content-width-plus-margin": true,
    })
    const primaryNavClassName = classNames({
        "sb-primary-nav": true,
        "sb-navbar-background": true,
        "sb-organization-color-primary-nav": true,
    })
    const primaryNavElementCellClassName = classNames({
        "sb-navbar-primary-element": true,
        "sb-navbar-margin-top": true,
        [styles.inlineBlock]: true,
    })

    return <nav id={"primaryNav"} className={primaryNavClassName} role="navigation">
        <div className={primaryNavContainerClassName}>
            <div className="sb-navbar-table">
                <div className="sb-navbar-row">
                    <div className={logoCellClassName} id={'logo-cell'}>
                        <div className={logoContainerClassName}>
                            {organizationStore.organization.logo ?
                                <ClickableLink href={props.mainUrl} aria-hidden={hideHeaderTitle}
                                               title={organizationStore.organization.title}
                                               className={styleStore.themeID === "1" ? "sb-organization-font-color sb-home-link" : ""}>
                                    <img className="sb-navbar-logo" src={organizationStore.organization.logo}
                                         alt={organizationStore.organization.title}/>
                                </ClickableLink> : <div className="sb-navbar-logo"/>}
                        </div>
                    </div>
                    <div className={titleAndSocialCellClassName} id={'title-cell'}>
                        <div className="sb-navbar-primary-element"
                             style={{borderLeft: "2px solid rgba(255,255,255,.5)", paddingLeft: "10px", float: "left"}}>
                                <ClickableLink href={props.mainUrl}
                                               className="sb-organization-font-color sb-home-link notranslate"
                                               title={organizationStore.organization.title}>
                                    <h1 className="sb-navbar-title"> {organizationStore.organization.title} </h1>
                                </ClickableLink>
                            <div id={"socialNavItems"}>
                                {organizationStore.organization.json_data?.settings?.social?.twitter &&
                                    <ClickableLink
                                        href={organizationStore.organization.json_data.settings.social.twitter}
                                        className="sb-organization-font-color" target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="Link to Twitter account"><span
                                        className="fab fa-twitter sb-navbar-social-media" role="presentation"
                                        aria-hidden="true"/></ClickableLink>}
                                {organizationStore.organization.json_data?.settings?.social?.facebook &&
                                    <ClickableLink
                                        href={organizationStore.organization.json_data.settings.social.facebook}
                                        className="sb-organization-font-color" target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="Link to Facebook account"><span
                                        className="fab fa-facebook-f sb-navbar-social-media" role="presentation"
                                        aria-hidden="true"/></ClickableLink>}
                                {organizationStore.organization.json_data?.settings?.social?.instagram &&
                                    <ClickableLink
                                        href={organizationStore.organization.json_data.settings.social.instagram}
                                        className="sb-organization-font-color" target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="Link to Instagram account"><span
                                        className="fab fa-instagram sb-navbar-social-media" role="presentation"
                                        aria-hidden="true"/></ClickableLink>}
                                {organizationStore.organization.json_data?.settings?.social?.youtube &&
                                    <ClickableLink
                                        href={organizationStore.organization.json_data.settings.social.youtube}
                                        className="sb-organization-font-color" target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="Link to Youtube account"><span
                                        className="fab fa-youtube sb-navbar-social-media" role="presentation"
                                        aria-hidden="true"/></ClickableLink>}
                            </div>
                        </div>
                    </div>
                    <div className="sb-navbar-cell" id={'search-cell'}>
                        <div className={primaryNavElementCellClassName}>
                            <SearchBox />
                        </div>
                    </div>
                    {!organizationStore.organization.json_data.settings.appearance.hideLoginButton &&
                        <div className="sb-navbar-cell" style={{whiteSpace: "nowrap"}} id={"loginUserMenuButtons"}>
                            <div className="sb-navbar-primary-element" style={{marginLeft: "1em", textAlign: "right"}}>
                                {userStore.id ?
                                    <button id="userMenuButton" onClick={props.toggleUserMenu} type="button"
                                            className="btn sb-organization-color-element-bg"
                                            style={{whiteSpace: "nowrap"}} aria-label="Open menu drawer">
                                        <div className="sb-navbar-button-image"
                                             style={{backgroundImage: buildCssUrlString(userStore.avatar)}}/>
                                        <div className="sb-navbar-button-text" aria-hidden="true">
                                            {userStore.firstName} {userStore.lastName}
                                        </div>
                                    </button> :
                                    <button id="loginMenuButton" className="btn sb-organization-color-element-bg"
                                            onClick={props.openLoginModal}>
                                        <span className="sb-navbar-button-text">Login</span>
                                    </button>}
                            </div>
                        </div>}
                </div>
            </div>
        </div>
        <div id={"secondaryNavLarge"} className={megaNavContainerClassName}>
            <div className="cd-main-header"><MegaNav/></div>
        </div>
    </nav>
});

export default SimpleAndFloating;
